import { useState, useEffect } from 'react';
import { GripVertical, Save, RotateCcw, AlertCircle, Check, ChevronUp, ChevronDown } from 'lucide-react';
import { SaveSetBestImageFront, GetImageOrder } from '../../Services/Settings';

const ImageOrderManager: React.FunctionComponent<{ onChangeMessagehandler: any }> = (props) => {

    const [categories, setCategories] = useState<string[]>([]);
    const [open, setOpen] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchOrder();
    }, []);

    const fetchOrder = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await GetImageOrder();

            if (response && response.imageorder && Array.isArray(response.imageorder)) {
                setCategories(response.imageorder);
            }
        } catch (err) {
            console.error('Error fetching order:', err);
            setError('Failed to load order');
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === index) return;

        const newCategories = [...categories];
        const draggedItem = newCategories[draggedIndex];

        newCategories.splice(draggedIndex, 1);
        newCategories.splice(index, 0, draggedItem);

        setCategories(newCategories);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSave = async () => {
        setSaving(true);
        SaveSetBestImageFront(categories).then(() => {
            props.onChangeMessagehandler("success", "Image order saved successfully.");
            setSaved(false);
            setSaving(false);
        }).catch((error: any) => {
            setSaved(false);
            setSaving(false);
            props.onChangeMessagehandler("error", error.response.data.message);
        });
    };

    const handleReset = () => {
        fetchOrder();
        setSaved(false);
    };

    const formatLabel = (text: string) => {
        return text
            .split(/[-_]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (loading) {
        return (
            <div className="cp-card mt-4 mb-4 padd-20">

                <div className="image-order-manager">
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading categories...</p>
                    </div>
                </div>
            </div>

        );
    }

    return (
        <div className="cp-card mt-4 mb-4 padd-20">
            <div className="manager-header d-flex align-items-center justify-content-between">
                <div>
                    <h2 className="manager-title">Set Best Image Front</h2>
                </div>
                <button
                    className="toggle-order-manager-btn"
                    onClick={() => setOpen((prev) => !prev)}
                    aria-label={open ? 'Close' : 'Open'}
                >
                    {open ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                </button>
            </div>

            {open && (

                <div className="image-order-manager mt-4">
                    <p className="manager-subtitle">Drag and drop to reorder image categories</p>
                    {error && (
                        <div className="error-banner">
                            <AlertCircle size={18} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="categories-list">
                        {categories.map((category, index) => (
                            <div
                                key={`${category}-${index}`}
                                className={`category-item ${draggedIndex === index ? 'dragging' : ''}`}
                                draggable
                                onDragStart={() => handleDragStart(index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <div className="drag-handle">
                                    <GripVertical size={20} />
                                </div>
                                <span className="category-number">{index + 1}</span>
                                <span className="category-label">{formatLabel(category)}</span>
                            </div>
                        ))}
                    </div>

                    <div className="manager-actions">
                        <button className="btn-reset" onClick={handleReset} disabled={saving}>
                            <RotateCcw size={18} />
                            Reset Order
                        </button>
                        <button className={`btn-save ${saved ? 'saved' : ''}`} onClick={handleSave} disabled={saving}>
                            {saved ? (
                                <>
                                    <Check size={18} />
                                    Saved!
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    {saving ? 'Saving...' : 'Save Order'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImageOrderManager;